import React from "react";
import { UilUpload, UilTimes } from "@iconscout/react-unicons";
import AddMovieHeader from "../components/AddMovieHeader";
import PageHeader from "../components/Header";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { uploadFile } from "../components/uploadFile";
import { toast } from "react-toastify";
import useMutation from "hooks/useMutation";
import { de } from "date-fns/locale";

export default function MovieArtwork({ onCancel, details }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const mediaId = searchParams.get("id")

  console.log(searchParams,details)

  const {
    control,
    handleSubmit,
    watch,
    setValue,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      landscape: null,
      portrait: null
    }
  });


  const landscapeFile = watch("landscape");
  const portraitFile = watch("portrait");

  React.useEffect(() => {
    let previewUrl;
    if (landscapeFile) {
      previewUrl = URL.createObjectURL(landscapeFile);
      return () => URL.revokeObjectURL(previewUrl);
    }
  }, [landscapeFile]);

  React.useEffect(() => {
    let previewUrl;
    if (portraitFile) {
      previewUrl = URL.createObjectURL(portraitFile);
      return () => URL.revokeObjectURL(previewUrl);
    }
  }, [portraitFile]);

  const removeFile = (type) => {
    setValue(type, null);
  };

  const handleCancel = () => {
    if (typeof onCancel === "function") onCancel();
    navigate(-1);
  };

  const { mutate, loading } = useMutation({ method: 'post', url: `/entertainment/movie-box/medias/${mediaId}/artworks` })

  const onSubmit = async (data) => {
    try {
      const backgroundPromise = data.landscape ? (
        uploadFile({
          name: 'background',
          file: data.landscape,
          mediaId: mediaId,
          type: 'artwork'
        })
      ) : Promise.resolve(null)

      const posterPromise = data.portrait ? (
        uploadFile({
          name: 'poster',
          file: data.portrait,
          mediaId: mediaId,
          type: 'artwork'
        })
      ) : Promise.resolve(null)

      const [bgRes, posterRes] = await Promise.all([backgroundPromise, posterPromise])

      const payload = {}
      if (bgRes?.key) payload.background = bgRes.key
      if (posterRes?.key) payload.poster = posterRes.key

      if (!Object.keys(payload).length) {
        toast.info("No artwork selected to upload")
        return
      }

      await mutate({
        payload,
        onSuccess: () => {
          toast.success("Artworks uploaded successfully")
          const isSeries = details?.media_type?.toLowerCase() === 'series'
          navigate(`/movie-box/add-movie/movie-upload/${isSeries ? 'episode' : 'movie-upload'}` + (mediaId ? `?id=${mediaId}` : ""))
        },
        onError: (error) => {
          console.error(error)
          toast.error("Error saving artworks")
        }
      })
    } catch (error) {
      console.log(error)
      toast.error(" Error uploading art work")
    }
  };

  const allUploaded = !!landscapeFile && !!portraitFile;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AddMovieHeader
        onCancel={handleCancel}
        showProgress={true}
        currentStep={3}
        continueDisabled={!allUploaded}
        onContinue={handleSubmit(onSubmit)}
        loading={loading}
      />

      <main className="flex-1 flex justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          <PageHeader
            title="Landscape"
            description="Add both landscape and portrait artwork to show your movie clearly across the app."
          />

          <form>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-8">
              {/* Landscape */}
              <div className="relative flex flex-col items-start">
                <span className="absolute -top-5 left-0 text-sm font-semibold text-gray-700">
                  Landscape
                </span>
                <div className="bg-blue-50 border-2 border-dashed rounded-md flex flex-col items-center justify-center h-72 w-full">
                  <Controller
                    name="landscape"
                    control={control}
                    render={({ field }) =>
                      field.value ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <img
                            src={URL.createObjectURL(field.value)}
                            alt="Landscape"
                            className="object-cover w-full h-full rounded-md"
                          />
                          <button
                            type="button"
                            onClick={() => removeFile("landscape")}
                            className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded-full"
                          >
                            <UilTimes size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="text-center px-2">
                          <p className="text-sm text-gray-600">1920px × 1080px</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Upload a 1920px × 1080px image, max 10MB file size.
                          </p>
                          <label className="mt-3 inline-flex items-center px-4 py-2 bg-white border rounded-md text-sm text-gray-800 cursor-pointer hover:bg-gray-100">
                            <UilUpload className="h-4 w-4 mr-2" />
                            Upload File
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                field.onChange(file || null);
                              }}
                            />
                          </label>
                        </div>
                      )
                    }
                  />
                </div>
              </div>
              {/* Portrait */}
              <div className="relative flex flex-col items-start">
                <span className="absolute -top-5 left-0 text-sm font-semibold text-gray-700">
                  Portrait
                </span>
                <div className="bg-blue-50 border-2 border-dashed rounded-md flex flex-col items-center justify-center h-72 max-w-[200px] w-full">
                  <Controller
                    name="portrait"
                    control={control}
                    render={({ field }) =>
                      field.value ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <img
                            src={URL.createObjectURL(field.value)}
                            alt="Portrait"
                            className="object-cover w-full h-full rounded-md"
                          />
                          <button
                            type="button"
                            onClick={() => removeFile("portrait")}
                            className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded-full"
                          >
                            <UilTimes size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="text-center px-2">
                          <p className="text-sm text-gray-600">600px × 800px</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Upload a 600px × 800px image, max 10MB file size.
                          </p>
                          <label className="mt-3 inline-flex items-center px-4 py-2 bg-white border rounded-md text-sm text-gray-800 cursor-pointer hover:bg-gray-100">
                            <UilUpload className="h-4 w-4 mr-2" />
                            Upload File
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                field.onChange(file || null);
                              }}
                            />
                          </label>
                        </div>
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
